import { Component } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { withTranslation } from 'react-i18next'

import TextField from '../../FormLegacy/FormFieldsV2/TextField'
import {
  selectBookCategoryById,
  deleteBookingCategory,
  createBookingCategory,
  editBookingCategory,
} from '../../../reducers/Organisation/bookCategoryReducer'
import ConfirmPopup from '../../Popups/ConfirmPopup'

export class BookingCategoryModal extends Component {
  static propTypes = {
    params: PropTypes.object,
    category: PropTypes.object.isRequired,
    onModalClose: PropTypes.func.isRequired,
    editBookingCategory: PropTypes.func.isRequired,
    createBookingCategory: PropTypes.func.isRequired,
    categoryOrder: PropTypes.number.isRequired,
    deleteBookingCategory: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
  }

  state = {
    formPending: false,
    formError: '',
    modalConfirmOpened: false,
  }

  closeModal = (e) => {
    this.props.onModalClose(e)
    this.setState({
      formPending: false,
    })
  }

  cancelServiceChanges = (e) => {
    e && e.preventDefault()
    this.closeModal(e)
  }

  isNew = () => this.props.params.categoryId === 'new'

  saveBookingCategory = async ({ name, order }) => {
    this.setState({ formPending: true, formError: '' })

    if (this.isNew()) {
      this.props
        .createBookingCategory({ name, order })
        .then(() => {
          this.closeModal()
          this.setState({ formPending: false })
        })
        .catch(({ message }) => {
          this.setState({
            formPending: false,
            formError: message,
          })
        })
    } else {
      const { order } = this.props.category
      this.props
        .editBookingCategory({ name, order }, this.props.params.categoryId)
        .then(() => {
          this.closeModal()
          this.setState({ formPending: false })
        })
        .catch(() => {
          this.setState({
            formPending: false,
            formError: this.props.t('organisation:shared.serverError'),
          })
        })
    }
  }

  openConfirmModal = (e) => {
    e.preventDefault()
    this.setState({ modalConfirmOpened: true })
  }

  closeConfirmModal = () => {
    this.setState({ modalConfirmOpened: false })
  }

  deleteBookCategory = (e) => {
    this.props
      .deleteBookingCategory(this.props.params.categoryId)
      .then(() => {
        this.closeConfirmModal()
        this.closeModal(e)
      })
      .catch(() => {
        this.setState({ formError: this.props.t('organisation:shared.serverError') })
        this.closeConfirmModal()
      })
  }

  render() {
    const { category, categoryOrder, t } = this.props
    this.validationSchema = Yup.object().shape({
      name: Yup.string().required(t('common:validation.required')),
    })
    return (
      <div
        className={`modal modal--organisation modal--service modal--sku ${
          !this.props.params.bookingId && this.props.params.categoryId ? 'is-active' : ''
        }`}
      >
        <div className="modal-background" onClick={this.cancelServiceChanges} />
        <div className="modal-content">
          <div className="box">
            <header className="modal__header">
              <span>{t('organisation:booking.category.title')}</span>
              <button
                className="modal-close is-large"
                aria-label={t('organisation:shared.closeAriaLabel')}
                onClick={(e) => this.cancelServiceChanges(e)}
              />
            </header>
            <Formik
              enableReinitialize={true}
              initialValues={{
                name: category.name || '',
                order: categoryOrder || null,
              }}
              onSubmit={this.saveBookingCategory}
            >
              {({
                handleChange,
                handleSubmit,
                values,
                touched,
                errors,
                dirty,
                setFieldTouched,
              }) => {
                return (
                  <form className="modal__inner form" onSubmit={handleSubmit}>
                    <div className="modal__section">
                      <div className="columns">
                        <div className="column is-12">
                          <TextField
                            name={'name'}
                            placeholder={t('organisation:booking.category.name.placeholder')}
                            label={t('organisation:booking.category.name.label')}
                            value={values.name}
                            onChange={handleChange}
                            isError={touched.name && !!errors.name}
                            onBlur={() => setFieldTouched('name')}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="modal__section modal__actions">
                      {!this.isNew() && (
                        <span className="link" onClick={this.openConfirmModal}>
                          {t('common:actions.delete')}
                        </span>
                      )}
                      <button
                        type="button"
                        className="button button-gray is-block"
                        onClick={(e) => this.cancelServiceChanges(e)}
                      >
                        {t('common:actions.cancel')}
                      </button>
                      <button
                        type="submit"
                        className={`button button-primary is-block ${
                          this.state.formPending ? 'is-loading' : ''
                        }`}
                        disabled={this.state.formPending || !dirty}
                      >
                        {this.isNew()
                          ? t('organisation:shared.actions.create')
                          : t('common:actions.save')}
                      </button>
                      <p className={'help is-danger'}>{this.state.formError}</p>
                    </div>
                  </form>
                )
              }}
            </Formik>
          </div>
        </div>
        <ConfirmPopup
          title={t('organisation:booking.category.deleteConfirm.title', { name: category.name })}
          cancelText={t('common:actions.cancel')}
          confirmText={t('common:actions.confirm')}
          isOpen={this.state.modalConfirmOpened}
          cancelCallback={this.closeConfirmModal}
          confirmCallback={this.deleteBookCategory}
        />
      </div>
    )
  }
}

export default connect(
  (state, { params }) => ({
    category: selectBookCategoryById(state)(params.categoryId),
  }),
  { deleteBookingCategory, createBookingCategory, editBookingCategory },
)(withTranslation(['organisation', 'common'])(BookingCategoryModal))
